import { useState, useEffect, useCallback } from "react";
import {
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  CheckIcon,
  XMarkIcon,
  PencilIcon,
  GlobeAltIcon,
  UserGroupIcon,
  MegaphoneIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";
import { useStudioPlan } from "../hooks/useStudioPlan";
import Notification from "../components/Notification";
import Tooltip from "../components/Tooltip";
import {
  validateFile,
  parseFile,
  sanitizeValue,
  getFileType,
} from "../utils/fileParser";
import DOMPurify from 'dompurify';
import { parsePlanLimitError } from "../plans/planLimitErrors";

type Lead = {
  id: string;
  created_at: string;
  updated_at: string;
  studio_id: string;
  name: string;
  phone: string | null;
  email: string | null;
  status: string | null;
  follow_up_date: string | null;
  source: string | null;
  notes: string | null;
  ai_conversion_probability: number | null;
  date_added: string | null;
  last_check_in: string | null;
  sms_consent: boolean | null;
  waiver_signed?: boolean;
  /** YYYY-MM-DD */
  birthdate?: string | null;
  gender?: string | null;
};

const statusColors = {
  New: "bg-blue-100 text-blue-800",
  Contacted: "bg-yellow-100 text-yellow-800",
  Scheduled: "bg-purple-100 text-purple-800",
  Qualified: "bg-green-100 text-green-800",
  Lost: "bg-red-100 text-red-800",
};

const sourceIcons = {
  Website: GlobeAltIcon,
  Referral: UserGroupIcon,
  "Social Media": MegaphoneIcon,
};

interface ExportField {
  key: keyof Lead;
  label: string;
  selected: boolean;
}

interface ImportField {
  key: keyof Lead;
  label: string;
  csvColumn: string;
}

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  name: 100,
  email: 254,
  phone: 20,
  status: 50,
  source: 100,
  notes: 2000,
  follow_up_date: 10,
  date_added: 10,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Enhanced input sanitization function (matching PublicWaiver.tsx approach)
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For text fields (names, notes), preserve spaces but remove dangerous content
  if (fieldType === 'name' || fieldType === 'notes') {
    // Only remove dangerous patterns, preserve spaces
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    // Don't trim for text fields - preserve user intent
  } else {
    // For other fields, use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Enhanced validation function (matching PublicWaiver.tsx approach)
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'email':
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(sanitized);
    case 'phone':
      const phoneRegex = /^[\d\s\-\(\)\.]+$/;
      return phoneRegex.test(sanitized) && sanitized.replace(/\D/g, '').length >= 10;
    case 'name':
    case 'notes':
      // Check for dangerous patterns instead of restrictive allowed characters
      const dangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !dangerousPatterns.test(sanitized) && sanitized.length >= 1;
    default:
      return sanitized.length > 0;
  }
};

// Enhanced security event logging (matching PublicWaiver.tsx approach)
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`LeadManagement Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

/** Highlight leads that still need a follow-up date set in the database. */
const leadNeedsFollowUp = (lead: Lead): boolean =>
  lead.follow_up_date == null || lead.follow_up_date.trim() === "";

const LeadScoreTooltipContent = () => (
  <div className="space-y-3">
    <p className="text-gray-300">AI estimates conversion chance from:</p>
    <ul className="space-y-1.5 text-white">
      <li className="flex gap-2">
        <span className="text-gray-400" aria-hidden="true">
          •
        </span>
        <span>Lead source</span>
      </li>
      <li className="flex gap-2">
        <span className="text-gray-400" aria-hidden="true">
          •
        </span>
        <span>Status</span>
      </li>
      <li className="flex gap-2">
        <span className="text-gray-400" aria-hidden="true">
          •
        </span>
        <span>Follow-up timing</span>
      </li>
      <li className="flex gap-2">
        <span className="text-gray-400" aria-hidden="true">
          •
        </span>
        <span>Recent activity</span>
      </li>
      <li className="flex gap-2">
        <span className="text-gray-400" aria-hidden="true">
          •
        </span>
        <span>Notes sentiment</span>
      </li>
    </ul>
  </div>
);

export default function LeadManagement() {
  const { user, session, getAuthToken } = useAuth();
  const { studioUser } = useStudioUser();
  const { hasCapability } = useStudioPlan();
  const showLeadScoring = hasCapability("aiLeadScoring");
  const [studioId, setStudioId] = useState<string | null>(null);
  const [studioTimezone, setStudioTimezone] = useState("America/New_York");
  
  // Enhanced security-related state (matching PublicWaiver.tsx approach)
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [waiverFilter, setWaiverFilter] = useState("all");
  const [smsConsentFilter, setSmsConsentFilter] = useState("all");
  const [sortField, setSortField] = useState<keyof Lead>(
    "ai_conversion_probability"
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [leadToConvert, setLeadToConvert] = useState<Lead | null>(null);
  const [exportFields, setExportFields] = useState<ExportField[]>([
    { key: "name", label: "Name", selected: true },
    { key: "email", label: "Email", selected: true },
    { key: "phone", label: "Phone", selected: true },
    { key: "status", label: "Status", selected: true },
    { key: "follow_up_date", label: "Follow up Date", selected: true },
    { key: "source", label: "Source", selected: true },
    { key: "notes", label: "Notes", selected: true },
    {
      key: "ai_conversion_probability",
      label: "Conversion Probability",
      selected: true,
    },
    { key: "date_added", label: "Date Added", selected: true },
    { key: "last_check_in", label: "Last Check In", selected: true },
    { key: "sms_consent", label: "SMS Consent", selected: true },
  ]);
  const [importFields, setImportFields] = useState<ImportField[]>([
    { key: "name", label: "Name", csvColumn: "" },
    { key: "email", label: "Email", csvColumn: "" },
    { key: "phone", label: "Phone", csvColumn: "" },
    { key: "status", label: "Status", csvColumn: "" },
    { key: "follow_up_date", label: "Follow Up Date", csvColumn: "" },
    { key: "source", label: "Source", csvColumn: "" },
    { key: "notes", label: "Notes", csvColumn: "" },
    {
      key: "ai_conversion_probability",
      label: "Conversion Probability",
      csvColumn: "",
    },
    { key: "date_added", label: "Date Added", csvColumn: "" },
    { key: "last_check_in", label: "Last Check In", csvColumn: "" },
    { key: "sms_consent", label: "SMS Consent", csvColumn: "" },
  ]);
  const [formData, setFormData] = useState<
    Omit<Lead, "id" | "created_at" | "updated_at" | "studio_id">
  >({
    name: "",
    phone: "",
    email: "",
    status: "",
    follow_up_date: "",
    source: "",
    date_added: "",
    notes: "",
    last_check_in: null,
    ai_conversion_probability: 0,
    sms_consent: null,
    birthdate: "",
    gender: "",
  });
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<number | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });
  const [lastCheckedInMap, setLastCheckedInMap] = useState<
    Record<string, string>
  >({});
  // Multi-select states
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Enhanced CSRF token fetching (matching PublicWaiver.tsx approach)
  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  // Multi-select handlers
  const toggleSelectMode = useCallback(() => {
    setIsSelectMode(!isSelectMode);
    if (isSelectMode) {
      setSelectedLeads(new Set());
    }
  }, [isSelectMode]);

  const toggleLeadSelection = useCallback((leadId: string) => {
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  }, []);



  const clearSelection = useCallback(() => {
    setSelectedLeads(new Set());
  }, []);

  const selectAllLeads = useCallback(() => {
    // Calculate current page leads here to avoid conditional hook calls
    const filteredLeads = leads.filter((lead) => {
      const matchesSearch = searchTerm === "" || 
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.phone && lead.phone.includes(searchTerm));
      
      const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
      const matchesWaiver = waiverFilter === "all" || 
        (waiverFilter === "signed" && lead.waiver_signed) ||
        (waiverFilter === "not_signed" && !lead.waiver_signed);
      const matchesSmsConsent = smsConsentFilter === "all" || 
        (smsConsentFilter === "consented" && lead.sms_consent) ||
        (smsConsentFilter === "not_consented" && !lead.sms_consent);
      
      return matchesSearch && matchesStatus && matchesWaiver && matchesSmsConsent;
    });

    const sortedLeads = [...filteredLeads].sort((a: Lead, b: Lead) => {
      if (sortField === "ai_conversion_probability") {
        return sortDirection === "asc"
          ? (a[sortField] as number) - (b[sortField] as number)
          : (b[sortField] as number) - (a[sortField] as number);
      }
      if (sortField === "name") {
        return sortDirection === "asc"
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      if (sortField === "created_at") {
        return sortDirection === "asc"
          ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          : new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      return 0;
    });

    const currentLeads = sortedLeads.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

    setSelectedLeads(new Set(currentLeads.map(lead => lead.id)));
  }, [leads, searchTerm, statusFilter, waiverFilter, smsConsentFilter, sortField, sortDirection, currentPage, itemsPerPage]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedLeads.size === 0) return;
    setShowBulkDeleteModal(true);
  }, [selectedLeads]);

  const confirmBulkDelete = useCallback(async () => {
    if (selectedLeads.size === 0) return;

    // Enhanced rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // Enhanced CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'bulkDeleteLeads', count: selectedLeads.size });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with bulk deletion');
      }
    }

    setLastSubmissionTime(now);
    setIsBulkDeleting(true);
    
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const leadsToDelete = leads.filter(lead => selectedLeads.has(lead.id));

      // Delete leads one by one
      const deletePromises = leadsToDelete.map(lead =>
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/leads/${lead.id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        })
      );

      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(result => result.status === 'fulfilled' && result.value.ok).length;
      const failed = results.length - successful;

      // Clear selection and exit select mode
      setSelectedLeads(new Set());
      setIsSelectMode(false);
      setShowBulkDeleteModal(false);

      // Refresh data by triggering the useEffect
      setRefreshTrigger(prev => prev + 1);

      if (failed === 0) {
        setNotification({
          show: true,
          message: `Successfully deleted ${successful} lead${successful !== 1 ? 's' : ''}`,
          title: "Bulk Delete Complete",
        });
      } else if (successful === 0) {
        setNotification({
          show: true,
          message: `Failed to delete ${failed} lead${failed !== 1 ? 's' : ''}`,
          title: "Bulk Delete Failed",
        });
      } else {
        setNotification({
          show: true,
          message: `Successfully deleted ${successful} lead${successful !== 1 ? 's' : ''}, failed to delete ${failed} lead${failed !== 1 ? 's' : ''}`,
          title: "Bulk Delete Partial",
        });
      }
    } catch (error) {
      console.error("Bulk delete error:", error);
      setNotification({
        show: true,
        message: "An error occurred during bulk delete",
        title: "Error",
      });
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedLeads, leads, getAuthToken, setRefreshTrigger]);

  // Update predefined status options
  const statusOptions = [
    "New",
    "Contacted",
    "Scheduled",
    "Qualified",
    "Negotiating",
    "Closed Won",
    "Closed Lost",
    "On Hold",
  ];

  // Update predefined source options
  const predefinedSources = [
    "Referral",
    "Local Partnerships",
    "Community Events & Booths",
    "Walk-in",
    "Internet Search",
    "Internet Ads",
    "Website Lead Capture",
    "Cross-Promotions",
    "Email Marketing",
    "Social Media",
    "Social Media Advertisements",
  ];

  // Fetch studioId from backend and CSRF token
  useEffect(() => {
    const authToken = getAuthToken();
    if (!user || !authToken) return;
    
    // Fetch studio ID and timezone
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setStudioId(data.id);
        setStudioTimezone(data.timezone || "America/New_York");
        setInitialLoading(false); // Turn off initial loading once studioId is set
      });
    
    // Fetch CSRF token
    fetchCsrfToken();
  }, [user, getAuthToken]);

  // Helper to fetch all leads across all pages
  const fetchAllLeads = async () => {
    const authToken = getAuthToken();
    if (!studioId || !authToken) return { data: [], total: 0 };
    const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/supabase/leads/${studioId}`;
    const baseParams = new URLSearchParams({
      page: "1",
      limit: "100", // backend cap is 100
      search: searchTerm,
      status: statusFilter === "all" ? "" : statusFilter,
      waiver: waiverFilter === "all" ? "" : waiverFilter,
      sortField,
      sortDirection,
    });

    const firstRes = await fetch(`${baseUrl}?${baseParams}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!firstRes.ok) return { data: [], total: 0 };
    const firstJson = await firstRes.json();
    let all = firstJson.data || [];
    const totalPages = firstJson.pagination?.totalPages || 1;
    const limitNum = firstJson.pagination?.limit || 100;
    if (totalPages > 1) {
      const pageFetches: Promise<Response>[] = [];
      for (let p = 2; p <= totalPages; p++) {
        const params = new URLSearchParams(baseParams);
        params.set("page", String(p));
        params.set("limit", String(limitNum));
        pageFetches.push(
          fetch(`${baseUrl}?${params.toString()}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
        );
      }
      const responses = await Promise.all(pageFetches);
      const jsons = await Promise.all(responses.map((r) => r.json()));
      for (const j of jsons) all = all.concat(j.data || []);
    }
    return { data: all, total: firstJson.pagination?.total || all.length };
  };

  // Fetch leads from backend with pagination (aggregate all pages)
  useEffect(() => {
    let canceled = false;
    const load = async () => {
      if (!studioId) return; // Don't start loading if no studioId yet
      setLeadsLoading(true);
      try {
        const { data, total } = await fetchAllLeads();
        if (canceled) return;
        setLeads(Array.isArray(data) ? data : []);
        setTotalLeadsCount(total || 0);
      } finally {
        if (!canceled) setLeadsLoading(false);
      }
    };
    load();
    return () => {
      canceled = true;
    };
  }, [studioId, getAuthToken, searchTerm, statusFilter, waiverFilter, smsConsentFilter, sortField, sortDirection, refreshTrigger]);

  // Fetch last checked-in dates for all leads
  useEffect(() => {
    const authToken = getAuthToken();
    if (!studioId || !authToken) return;
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/attendance/last-checked-in/${studioId}?type=lead`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setLastCheckedInMap(data || {});
      })
      .catch(() => setLastCheckedInMap({}));
  }, [studioId, getAuthToken]);

  // Check if user is authenticated
  if (!user) {
    return <div>Please log in to access this page.</div>;
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEditClick = (lead: Lead) => {
    const { id, created_at, updated_at, studio_id, ...leadData } = lead;
    
    // Convert database date format to MM/DD/YYYY for display
    const formattedLeadData = {
      ...leadData,
      follow_up_date: convertDatabaseDateToDisplay(leadData.follow_up_date),
      date_added: convertDatabaseDateToDisplay(leadData.date_added),
      // Format phone number to XXX-XXX-XXXX for display
      phone: leadData.phone ? formatPhoneNumber(leadData.phone) : '',
    };
    
    setFormData(formattedLeadData);
    setEditingLead(lead);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingLead(null);
    setFormData({
      name: "",
      phone: "",
      email: "",
      status: "",
      follow_up_date: "",
      source: "",
      date_added: "",
      notes: "",
      last_check_in: null,
      ai_conversion_probability: 0,
      sms_consent: null,
      birthdate: "",
      gender: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const authToken = getAuthToken();
    if (!studioId || !authToken) return;

    // Enhanced rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // Enhanced CSRF token validation (matching PublicWaiver.tsx approach)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'submitLead' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Enhanced input validation
    if (!validateInput(formData.name || '', 'name')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'name', value: formData.name });
      setNotification({
        show: true,
        message: "Invalid lead name",
        title: "Error",
      });
      return;
    }

    if (formData.email && !validateInput(formData.email, 'email')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'email', value: formData.email });
      setNotification({
        show: true,
        message: "Invalid email address",
        title: "Error",
      });
      return;
    }

    if (formData.phone && !validateInput(formData.phone, 'phone')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'phone', value: formData.phone });
      setNotification({
        show: true,
        message: "Invalid phone number",
        title: "Error",
      });
      return;
    }

    if (formData.notes && !validateInput(formData.notes, 'notes')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'notes', value: formData.notes?.substring(0, 100) + '...' });
      setNotification({
        show: true,
        message: "Invalid notes content",
        title: "Error",
      });
      return;
    }

    setLastSubmissionTime(now);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Enhanced input sanitization
      const sanitizedLeadData = {
        name: sanitizeInput(formData.name || '', 'name'),
        email: sanitizeInput(formData.email || '', 'email'),
        phone: sanitizeInput(formData.phone || '', 'phone'),
        status: sanitizeInput(formData.status || '', 'status'),
        source: sanitizeInput(formData.source || '', 'source'),
        notes: sanitizeInput(formData.notes || '', 'notes'),
        studio_id: studioId,
        // Convert MM/DD/YYYY format back to ISO format for database
        follow_up_date: convertDisplayDateToDatabase(formData.follow_up_date || ''),
        date_added: editingLead
          ? convertDisplayDateToDatabase(formData.date_added || '')
          : new Date().toISOString(),
        last_check_in: formData.last_check_in || null,
        ai_conversion_probability: formData.ai_conversion_probability || 0,
        sms_consent: formData.sms_consent,
        waiver_signed: formData.waiver_signed,
        // Carried over to the student when the lead is converted.
        birthdate: /^\d{4}-\d{2}-\d{2}$/.test(formData.birthdate || '')
          ? formData.birthdate
          : null,
        gender: formData.gender === 'Male' || formData.gender === 'Female'
          ? formData.gender
          : null,
      };

      if (editingLead) {
        // Update existing lead
        const response = await fetch(
          `${apiUrl}/api/supabase/leads/${editingLead.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify(sanitizedLeadData),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to update lead");
        }

        setNotification({
          show: true,
          message: "Lead updated successfully",
          title: "Success",
        });
        if (editingLead) {
          const updatedLead = await response.json();
          setEditingLead(updatedLead);
          setFormData((prev) => ({ ...prev, ...updatedLead }));
        }
      } else {
        // Create new lead
        const response = await fetch(`${apiUrl}/api/supabase/leads`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(sanitizedLeadData),
        });

        if (!response.ok) {
          const limitError = await parsePlanLimitError(response, 1);
          if (limitError) {
            setNotification({
              show: true,
              message: limitError.message,
              title: limitError.title,
            });
            return;
          }
          throw new Error("Failed to create lead");
        }

        setNotification({
          show: true,
          message: "Lead created successfully",
          title: "Success",
        });
      }

      handleModalClose();
      // Refresh data by triggering the useEffect
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      setNotification({
        show: true,
        message:
          error instanceof Error && error.message !== "Failed to create lead"
            ? error.message
            : "Failed to save lead. Please try again.",
        title: "Error",
      });
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    let newValue = value;

    if (name === "phone") {
      // Remove all non-digit characters
      const digits = value.replace(/\D/g, "");
      // Format as XXX-XXX-XXXX
      if (digits.length <= 3) {
        newValue = digits;
      } else if (digits.length <= 6) {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else if (digits.length <= 10) {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6
        )}`;
      } else {
        newValue = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6,
          10
        )}`;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleDeleteLead = (lead: Lead) => {
    setLeadToDelete(lead);
  };

  const confirmDelete = async () => {
    if (!leadToDelete) return;

    // Enhanced rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // Enhanced CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteLead', leadId: leadToDelete.id });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with deletion');
      }
    }

    setLastSubmissionTime(now);

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${apiUrl}/api/supabase/leads/${leadToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete lead");
      }

      setNotification({
        show: true,
        message: "Lead deleted successfully",
        title: "Success",
      });
      setLeadToDelete(null);
      // Refresh data by triggering the useEffect
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to delete lead",
        title: "Error",
      });
    }
  };

  const cancelDelete = () => {
    setLeadToDelete(null);
  };

  const handleExport = async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setNotification({
        show: true,
        message: "No active session",
        title: "Error",
      });
      return;
    }
    try {
      // Fetch ALL leads for export
      const { data: allLeads } = await fetchAllLeads();

      // Filter selected fields
      const selectedFields = exportFields.filter((field) => field.selected);
      const headers = selectedFields.map((field) => field.label);
      const rows = (allLeads || []).map((lead: Lead) =>
        selectedFields.map((field) => lead[field.key])
      );

      // Create CSV content
      const csvContent = [
        headers.join(","),
        ...rows.map((row: any[]) => row.join(",")),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `leads_export_${new Date().toISOString()}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setNotification({
        show: true,
        message: "Leads exported successfully",
        title: "Success",
      });
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to export leads",
        title: "Error",
      });
    }
  };

  const toggleExportField = (index: number) => {
    setExportFields((prev) =>
      prev.map((field, i) =>
        i === index ? { ...field, selected: !field.selected } : field
      )
    );
  };

  const handleImportClick = () => {
    setIsImportModalOpen(true);
  };

  const handleImportFieldChange = (index: number, value: string) => {
    setImportFields((prev) =>
      prev.map((field, i) =>
        i === index ? { ...field, csvColumn: value } : field
      )
    );
  };

  const handleDownloadTemplate = () => {
    // Create CSV template with headers and two sample rows
    const headers = importFields.map((field) => field.label);
    const sampleData = [
      [
        "John Doe",
        "john.doe@example.com",
        "(555) 123-4567",
        "New",
        "2024-06-01",
        "Website Lead Capture",
        "Interested in kids classes",
        "0.75",
        "2024-05-20",
        "2024-05-25",
        "true",
      ],
      [
        "Jane Smith",
        "jane.smith@example.com",
        "(555) 987-6543",
        "Contacted",
        "2024-06-05",
        "Referral",
        "Referred by a friend, prefers evenings",
        "0.60",
        "2024-05-22",
        "2024-05-28",
        "false",
      ],
    ];
    
    // Create CSV content with proper escaping
    const escapeCSV = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };
    
    const csvContent = [
      headers.map(escapeCSV).join(","),
      ...sampleData.map((row) => row.map(escapeCSV).join(",")),
    ].join("\n");

    // Create and download CSV file
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "leads_import_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConvertClick = (lead: Lead) => {
    setLeadToConvert(lead);
    setIsConvertModalOpen(true);
  };

  const handleConvertConfirm = async () => {
    if (!leadToConvert || !studioId) return;

    // Enhanced rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // Enhanced CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'convertLead', leadId: leadToConvert.id });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with conversion');
      }
    }

    setLastSubmissionTime(now);

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Convert lead to student
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${apiUrl}/api/supabase/leads/${leadToConvert.id}/convert`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            studio_id: studioId,
          }),
        }
      );

      if (!response.ok) {
        const limitError = await parsePlanLimitError(response, 1);
        if (limitError) {
          setNotification({
            show: true,
            message: limitError.message,
            title: limitError.title,
          });
          setLeadToConvert(null);
          return;
        }
        throw new Error("Failed to convert lead");
      }

      setNotification({
        show: true,
        message: "Lead converted to student successfully",
        title: "Success",
      });

      setLeadToConvert(null);
      // Refresh data by triggering the useEffect
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      setNotification({
        show: true,
        message:
          error instanceof Error && error.message !== "Failed to convert lead"
            ? error.message
            : "Failed to convert lead to student. Please try again.",
        title: "Error",
      });
    }
  };

  const handleConvertCancel = () => {
    setLeadToConvert(null);
    setIsConvertModalOpen(false);
  };

  const handleSort = (field: keyof Lead) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  function formatDate(dateString: string | null) {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  }

  function formatPhoneNumber(phone: string | null): string {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phone;
  }

  // File validation is now handled by the utility function

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const validation = validateFile(file);
        if (!validation.isValid) {
          throw new Error(validation.error || "Invalid file");
        }

        setImportFile(file);
        setImportError(null);
        setImportSuccess(null);

        // Parse the file to extract headers
        const parsedData = await parseFile(file);

        if (parsedData.errors.length > 0) {
          setImportError(parsedData.errors.join(", "));
          return;
        }

        if (parsedData.headers.length === 0) {
          setImportError("No headers found in file");
          return;
        }



        // Auto-map columns
        setImportFields((prevFields) =>
          prevFields.map((field) => {
            const match = parsedData.headers.find(
              (header) =>
                header.replace(/\s+/g, "").toLowerCase() ===
                field.label.replace(/\s+/g, "").toLowerCase()
            );
            return {
              ...field,
              csvColumn: match || "",
            };
          })
        );
      } catch (error) {
        setImportError(error instanceof Error ? error.message : "Invalid file");
        setImportFile(null);
      }
    }
  };

  // CSV injection prevention is now handled by the utility function

  const handleImportSubmit = async () => {
    if (!importFile || !studioId) return;
    const authToken = getAuthToken();
    if (!authToken) return;

    // Enhanced rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // Enhanced CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'importLeads' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with import');
      }
    }

    setLastSubmissionTime(now);
    setIsImporting(true);
    setImportError(null);
    setImportSuccess(null);

    try {
      // Parse the file using our utility
      const parsedData = await parseFile(importFile);

      if (parsedData.errors.length > 0) {
        setImportError(parsedData.errors.join(", "));
        return;
      }

      // List of valid columns in the leads table
      const validColumns = [
        "name",
        "email",
        "phone",
        "status",
        "source",
        "notes",
        "ai_drop_off_risk",
        "studio_id",
      ];

      // Map file columns to DB field names using importFields and filter to valid columns
      const leadsToImport = parsedData.rows.map((lead: any) => {
        const mappedLead: any = {};
        importFields.forEach((field) => {
          if (field.csvColumn && lead[field.csvColumn] !== undefined) {
            // Enhanced sanitization using our security function
            const sanitizedValue = sanitizeInput(lead[field.csvColumn], field.key);
            mappedLead[field.key] = sanitizedValue;
          }
        });
        mappedLead.studio_id = studioId;

        // Filter to only valid columns
        const filteredLead: any = {};
        Object.keys(mappedLead).forEach((key) => {
          if (validColumns.includes(key)) {
            filteredLead[key] = mappedLead[key];
          }
        });
        return filteredLead;
      });

      // Insert leads in batches of 100
      const batchSize = 100;
      let resultsArr: any[] = [];
      for (let i = 0; i < leadsToImport.length; i += batchSize) {
        const batch = leadsToImport.slice(i, i + batchSize);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/leads`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify(batch),
          }
        );
        if (!response.ok) {
          const limitError = await parsePlanLimitError(response, batch.length);
          if (limitError) {
            setImportError(limitError.message);
            setNotification({
              show: true,
              message: limitError.message,
              title: limitError.title,
            });
            return;
          }
          throw new Error("Failed to import leads");
        }
        const data = await response.json();
        resultsArr = resultsArr.concat(data || []);
      }

      setImportSuccess(resultsArr.length);
      setNotification({
        show: true,
        message: `Successfully imported ${resultsArr.length} leads`,
        title: "Success",
      });

      // Refresh data by triggering the useEffect
      setRefreshTrigger(prev => prev + 1);
      setIsImportModalOpen(false);
      setImportFile(null);
    } catch (error) {
      setImportError(
        error instanceof Error ? error.message : "Failed to import leads"
      );
    } finally {
      setIsImporting(false);
    }
  };

  // Filter leads based on search term, status, waiver, and SMS consent
  const filteredLeads = leads.filter((lead: Lead) => {
    const matchesSearch = searchTerm === "" || 
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (lead.phone && lead.phone.includes(searchTerm));
    
    const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
    const matchesWaiver = waiverFilter === "all" || 
      (waiverFilter === "signed" && lead.waiver_signed) ||
      (waiverFilter === "not_signed" && !lead.waiver_signed);
    const matchesSmsConsent = smsConsentFilter === "all" || 
      (smsConsentFilter === "consented" && lead.sms_consent) ||
      (smsConsentFilter === "not_consented" && !lead.sms_consent);
    
    return matchesSearch && matchesStatus && matchesWaiver && matchesSmsConsent;
  });

  // Sort leads
  const sortedLeads = [...filteredLeads].sort((a: Lead, b: Lead) => {
    if (sortField === "ai_conversion_probability") {
      return sortDirection === "asc"
        ? (a[sortField] as number) - (b[sortField] as number)
        : (b[sortField] as number) - (a[sortField] as number);
    }
    if (sortField === "name") {
      return sortDirection === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    if (sortField === "created_at") {
      return sortDirection === "asc"
        ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        : new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    return 0;
  });

  // Calculate pagination
  const totalLeads = sortedLeads.length;
  const totalPages = Math.ceil(totalLeads / itemsPerPage);
  const currentLeads = sortedLeads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );



  // Utility function for consistent input styling
const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white text-gray-900`;
};

// Date input formatter function
const formatDateInput = (value: string): string => {
  // Remove all non-digits
  const numbers = value.replace(/\D/g, '');
  
  // Limit to 8 digits (MMDDYYYY)
  const limited = numbers.slice(0, 8);
  
  // Format as MM/DD/YYYY
  if (limited.length >= 5) {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  } else if (limited.length >= 3) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else if (limited.length >= 1) {
    return limited;
  }
  
  return '';
};

// Convert database date format to MM/DD/YYYY
const convertDatabaseDateToDisplay = (dateString: string | null): string => {
  if (!dateString) return '';
  
  try {
    // Handle ISO date strings like "2025-07-18T00:00:00+00:00"
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    return '';
  }
};

// Convert MM/DD/YYYY format to ISO date string for database
const convertDisplayDateToDatabase = (dateString: string): string | null => {
  if (!dateString || dateString.trim() === '') return null;
  
  try {
    // Parse MM/DD/YYYY format
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;
    
    const month = parseInt(parts[0]) - 1; // Month is 0-indexed
    const day = parseInt(parts[1]);
    const year = parseInt(parts[2]);
    
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return null;
    
    return date.toISOString();
  } catch (error) {
    return null;
  }
};

  return (
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Lead Management
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Track and manage potential new students
            </p>
          </div>
          <div className="flex flex-col xl:flex-row xl:items-center gap-3 xl:space-x-4">
            <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <UserIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {totalLeadsCount} lead{totalLeadsCount !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 xl:space-x-3">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" aria-hidden="true" />
              <span className="pointer-events-none">Add Lead</span>
            </button>
            <button
              type="button"
              onClick={handleImportClick}
              className="inline-flex items-center rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ArrowUpTrayIcon
                className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none"
                aria-hidden="true"
              />
              <span className="pointer-events-none">Import Leads</span>
            </button>
            <button
              type="button"
              onClick={() => setIsExportModalOpen(true)}
              className="inline-flex items-center rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ArrowDownTrayIcon
                className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none"
                aria-hidden="true"
              />
              <span className="pointer-events-none">Export Leads</span>
            </button>
            </div>
          </div>
        </div>

        {/* Multi-select controls and Search */}
        <div className="space-y-3">
          {isSelectMode && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={selectedLeads.size === 0}
                className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
                  selectedLeads.size === 0
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-red-600 text-white hover:bg-red-700"
                }`}
              >
                <TrashIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" />
                <span className="pointer-events-none">Delete Selected ({selectedLeads.size})</span>
              </button>
              <button
                type="button"
                onClick={clearSelection}
                disabled={selectedLeads.size === 0}
                className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
                  selectedLeads.size === 0
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-gray-600 text-white hover:bg-gray-700"
                }`}
              >
                <span className="pointer-events-none">Clear Selection</span>
              </button>
            </div>
          )}

          <div className="flex items-center gap-3 w-full">
            <div className="flex-1 min-w-0">
              <input
                type="text"
                className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder="Search leads by name, email, or phone..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>

            <button
              type="button"
              onClick={toggleSelectMode}
              className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm transition-colors flex-shrink-0 justify-center ${
                isSelectMode
                  ? "bg-gray-600 text-white hover:bg-gray-700"
                  : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <span className="pointer-events-none">{isSelectMode ? "Exit Select Mode" : "Delete Leads"}</span>
            </button>
          </div>
        </div>

        {/* Sort Controls */}
        <div className="flex flex-wrap items-center gap-2 xl:space-x-2 mt-4">
            <select
              value={waiverFilter}
              onChange={(e) => {
                setWaiverFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="block rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            >
              <option value="all">Waiver Statuses</option>
              <option value="signed">Waiver Signed</option>
              <option value="not_signed">No Waiver Signed</option>
            </select>
            <select
              value={smsConsentFilter}
              onChange={(e) => {
                setSmsConsentFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="block rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            >
              <option value="all">SMS Consent</option>
              <option value="consented">SMS Consented</option>
              <option value="not_consented">No SMS Consent</option>
            </select>
          <button
            type="button"
            onClick={() => handleSort("name")}
            className={`px-3 py-2 text-sm rounded-md font-semibold border border-gray-300 dark:border-gray-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors ${
              sortField === "name"
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            <span className="pointer-events-none">
              Name{" "}
              {sortField === "name" && (sortDirection === "asc" ? "↑" : "↓")}
            </span>
          </button>
          <button
            type="button"
            onClick={() => handleSort("created_at")}
            className={`px-3 py-2 text-sm rounded-md font-semibold border border-gray-300 dark:border-gray-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors ${
              sortField === "created_at"
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            <span className="pointer-events-none">
              Date Added{" "}
              {sortField === "created_at" &&
                (sortDirection === "asc" ? "↑" : "↓")}
            </span>
          </button>
          <button
            type="button"
            onClick={() => handleSort("ai_conversion_probability")}
            className={`px-3 py-2 text-sm rounded-md font-semibold border border-gray-300 dark:border-gray-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors ${
              sortField === "ai_conversion_probability"
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            <span className="pointer-events-none">
              Conversion %{" "}
              {sortField === "ai_conversion_probability" &&
                (sortDirection === "asc" ? "↑" : "↓")}
            </span>
          </button>
        </div>

        {/* Loading Message */}
        {(initialLoading || leadsLoading) && (
          <div className="mt-8 flex justify-center items-center py-8">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Loading lead information...
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Lead Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleModalClose}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 sm:ml-4 sm:mt-0 w-full text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      {editingLead ? "Edit Lead" : "Add New Lead"}
                    </h3>
                    <div className="mt-2">
                      <form className="space-y-4" onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label
                              htmlFor="name"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="name"
                              id="name"
                              required
                              value={formData.name || ""}
                              onChange={handleInputChange}
                              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>
                          {showLeadScoring && editingLead ? (
                            <div>
                              <Tooltip text={<LeadScoreTooltipContent />}>
                                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 cursor-help">
                                  Lead Score
                                </span>
                              </Tooltip>
                              {(() => {
                                const score =
                                  editingLead.ai_conversion_probability ??
                                  formData.ai_conversion_probability ??
                                  0;
                                const percent = Math.round(
                                  Math.min(Math.max(score, 0), 1) * 100
                                );
                                const level =
                                  score < 0.3
                                    ? {
                                        label: "Low",
                                        bar: "bg-red-500",
                                        text: "text-red-700 dark:text-red-400",
                                      }
                                    : score < 0.7
                                    ? {
                                        label: "Medium",
                                        bar: "bg-yellow-500",
                                        text: "text-yellow-700 dark:text-yellow-400",
                                      }
                                    : {
                                        label: "High",
                                        bar: "bg-green-500",
                                        text: "text-green-700 dark:text-green-400",
                                      };
                                return (
                                  <div className="mt-1 flex h-[38px] items-center gap-3">
                                    <div
                                      className="h-2.5 flex-1 rounded-full bg-gray-200 dark:bg-gray-600"
                                      role="meter"
                                      aria-label="Lead conversion score"
                                      aria-valuemin={0}
                                      aria-valuemax={100}
                                      aria-valuenow={percent}
                                    >
                                      <div
                                        className={`h-2.5 rounded-full ${level.bar}`}
                                        style={{ width: `${percent}%` }}
                                      />
                                    </div>
                                    <span className="text-sm tabular-nums text-gray-700 dark:text-gray-300">
                                      {percent}%
                                    </span>
                                    <span
                                      className={`text-xs font-medium ${level.text}`}
                                    >
                                      {level.label}
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                          ) : null}
                          <div>
                            <label
                              htmlFor="email"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              Email <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              name="email"
                              id="email"
                              value={formData.email || ""}
                              onChange={handleInputChange}
                              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="birthdate"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              Birthday
                            </label>
                            <input
                              type="date"
                              name="birthdate"
                              id="birthdate"
                              max={new Date().toISOString().split("T")[0]}
                              value={formData.birthdate || ""}
                              onChange={handleInputChange}
                              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="gender"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              Gender
                            </label>
                            <select
                              name="gender"
                              id="gender"
                              value={formData.gender || ""}
                              onChange={handleInputChange}
                              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              <option value="">Select Gender</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                            </select>
                          </div>
                          <div>
                            <label
                              htmlFor="phone"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              Phone
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              id="phone"
                              value={formData.phone || ""}
                              onChange={handleInputChange}
                              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="source"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              Source <span className="text-red-500">*</span>
                            </label>
                            <select
                              id="source"
                              name="source"
                              required
                              value={formData.source || ""}
                              onChange={handleInputChange}
                              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              <option value="">Select a source</option>
                              {predefinedSources.map((source) => (
                                <option key={source} value={source}>
                                  {source}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label
                              htmlFor="status"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              Status <span className="text-red-500">*</span>
                            </label>
                            <select
                              id="status"
                              name="status"
                              required
                              value={formData.status || ""}
                              onChange={handleInputChange}
                              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              <option value="">Select a status</option>
                              {statusOptions.map((status) => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label
                              htmlFor="follow_up_date"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              Follow Up Date
                              {studioTimezone && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                                  {studioTimezone}
                                </span>
                              )}
                            </label>
                            <div className="flex space-x-2">
                              <input
                                type="text"
                                name="follow_up_date"
                                id="follow_up_date"
                                value={formData.follow_up_date || ""}
                                onChange={(e) => {
                                  const formattedValue = formatDateInput(e.target.value);
                                  setFormData(prev => ({
                                    ...prev,
                                    follow_up_date: formattedValue
                                  }));
                                }}
                                placeholder="MM/DD/YYYY"
                                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  // Use studio timezone if available, otherwise use local timezone
                                  const timezone = studioTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                                  const today = new Date();
                                  
                                  // Format date in MM/DD/YYYY format
                                  const month = String(today.getMonth() + 1).padStart(2, '0');
                                  const day = String(today.getDate()).padStart(2, '0');
                                  const year = today.getFullYear();
                                  const todayFormatted = `${month}/${day}/${year}`;
                                  
                                  setFormData(prev => ({
                                    ...prev,
                                    follow_up_date: todayFormatted
                                  }));
                                }}
                                className="mt-1 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                                title={studioTimezone ? `Today in ${studioTimezone}` : 'Today (local time)'}
                              >
                                <span className="pointer-events-none">Today</span>
                              </button>
                            </div>
                          </div>
                          <div className="sm:col-span-2">
                            <label
                              htmlFor="notes"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                              Notes
                            </label>
                            <textarea
                              id="notes"
                              name="notes"
                              rows={3}
                              value={formData.notes || ""}
                              onChange={handleInputChange}
                              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          {/* SMS Consent Toggle */}
                          <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              SMS Communication Consent
                            </label>
                            <div
                              className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border transition-colors duration-150 cursor-pointer select-none ${
                                formData.sms_consent
                                  ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700"
                                  : "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600"
                              }`}
                              onClick={() =>
                                setFormData((prev: any) => ({
                                  ...prev,
                                  sms_consent: !prev.sms_consent,
                                }))
                              }
                              tabIndex={0}
                              role="button"
                              aria-pressed={!!formData.sms_consent}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ")
                                  setFormData((prev: any) => ({
                                    ...prev,
                                    sms_consent: !prev.sms_consent,
                                  }));
                              }}
                            >
                              {formData.sms_consent ? (
                                <>
                                  <span className="text-green-600 dark:text-green-400 mr-2">📱</span>
                                  SMS Consent Provided
                                </>
                              ) : (
                                <>
                                  <span className="text-gray-400 mr-2">📵</span>
                                  No SMS Consent Provided
                                </>
                              )}
                            </div>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Waiver Status
                            </label>
                            <div
                              className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border transition-colors duration-150 cursor-pointer select-none ${
                                formData.waiver_signed
                                  ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700"
                                  : "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600"
                              }`}
                              onClick={() =>
                                setFormData((prev: any) => ({
                                  ...prev,
                                  waiver_signed: !prev.waiver_signed,
                                }))
                              }
                              tabIndex={0}
                              role="button"
                              aria-pressed={!!formData.waiver_signed}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ")
                                  setFormData((prev: any) => ({
                                    ...prev,
                                    waiver_signed: !prev.waiver_signed,
                                  }));
                              }}
                            >
                              {formData.waiver_signed ? (
                                <>
                                  <span className="text-green-600 dark:text-green-400 mr-2">
                                    ✔️
                                  </span>
                                  Waiver Signed
                                </>
                              ) : (
                                <>
                                  <span className="text-gray-400 mr-2">🚫</span>
                                  No Waiver Signed
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                          <button
                            type="submit"
                            className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                          >
                            <span className="pointer-events-none">{editingLead ? "Update" : "Add Lead"}</span>
                          </button>
                          <button
                            type="button"
                            className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                            onClick={handleModalClose}
                          >
                            <span className="pointer-events-none">Cancel</span>
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Modal */}
        {isExportModalOpen && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => setIsExportModalOpen(false)}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 sm:ml-4 sm:mt-0 w-full text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Export Leads
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Select the fields you want to include in the export:
                      </p>
                      <div className="mt-4 space-y-2">
                        {exportFields.map((field, index) => (
                          <div key={field.key} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`export-${field.key}`}
                              checked={field.selected}
                              onChange={() => toggleExportField(index)}
                              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-600 dark:bg-gray-700 dark:checked:bg-indigo-600"
                            />
                            <label
                              htmlFor={`export-${field.key}`}
                              className="ml-3 text-sm text-gray-700 dark:text-gray-300"
                            >
                              {field.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleExport}
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">Export</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsExportModalOpen(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {isImportModalOpen && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => {
                      setIsImportModalOpen(false);
                      setImportFile(null);
                      setImportError(null);
                      setImportSuccess(null);
                    }}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 sm:ml-4 sm:mt-0 w-full text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Import Leads
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Upload a CSV, Excel (.xlsx, .xls), or JSON file with
                        your leads data. Make sure the column headers match the
                        fields below.
                      </p>
                      <div className="mt-4 space-y-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Instructions:
                          </h4>
                          <ul className="mt-2 list-disc list-inside text-sm text-gray-500 dark:text-gray-400 space-y-1">
                            <li>
                              Download the template file to see the required
                              format
                            </li>
                            <li>
                              Fill in your lead data following the template
                            </li>
                            <li>
                              Map your file columns to the system fields below
                            </li>
                            <li>
                              Upload your file (CSV, Excel, or JSON) to import
                              the leads
                            </li>
                          </ul>
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={handleDownloadTemplate}
                            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                          >
                            <ArrowDownTrayIcon
                              className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none"
                              aria-hidden="true"
                            />
                            <span className="pointer-events-none">Download Template</span>
                          </button>
                        </div>
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Upload File
                          </label>
                          <div className="mt-1">
                            <input
                              type="file"
                              accept=".csv,.xlsx,.xls,.json"
                              onChange={handleFileUpload}
                              className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 dark:file:bg-indigo-900/30 file:text-indigo-700 dark:file:text-indigo-400 hover:file:bg-indigo-100 dark:hover:file:bg-indigo-800/50"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Field Mapping
                          </label>
                          <div className="mt-2 space-y-2">
                            {importFields.map((field, index) => (
                              <div
                                key={field.key}
                                className="flex items-center"
                              >
                                <label className="block w-1/3 text-sm text-gray-700 dark:text-gray-300">
                                  {field.label}
                                </label>
                                <input
                                  type="text"
                                  value={field.csvColumn}
                                  onChange={(e) =>
                                    handleImportFieldChange(
                                      index,
                                      e.target.value
                                    )
                                  }
                                  placeholder="File Column Header"
                                  className="block w-2/3 rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                />
                              </div>
                            ))}
                          </div>
                          {/* Add notes for special fields */}
                          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                            <div>
                              <strong>Note:</strong> For{" "}
                              <em>Conversion Probability</em>, enter percentages
                              as decimals (e.g., <code>0.65</code> for 65%,{" "}
                              <code>1</code> for 100%).
                            </div>
                            <div>
                              <strong>SMS Consent:</strong> Use{" "}
                              <code>true</code>, <code>false</code>,{" "}
                              <code>yes</code>, or <code>no</code> to indicate
                              SMS consent status.
                            </div>
                          </div>
                        </div>
                      </div>
                      {importError && (
                        <div className="mt-4 text-sm text-red-600 dark:text-red-400">
                          {importError}
                        </div>
                      )}
                      {importSuccess !== null && <></>}
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleImportSubmit}
                    disabled={!importFile || isImporting}
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">{isImporting ? "Importing..." : "Import"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsImportModalOpen(false);
                      setImportFile(null);
                      setImportError(null);
                      setImportSuccess(null);
                    }}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leads Table */}
        {!initialLoading && !leadsLoading && (
          <div className="mt-8 relative">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full py-2 align-middle">
                <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-0 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          {isSelectMode && (
                            <input
                              type="checkbox"
                              checked={selectedLeads.size === currentLeads.length && currentLeads.length > 0}
                              onChange={() => {
                                if (selectedLeads.size === currentLeads.length) {
                                  clearSelection();
                                } else {
                                  selectAllLeads();
                                }
                              }}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-3"
                            />
                          )}
                          Name
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          Contact
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          <Tooltip text={<LeadScoreTooltipContent />}>
                            <span className="cursor-help">
                              AI Conversion Probability
                            </span>
                          </Tooltip>
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          Follow Up
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          Last Check In
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          Date Added
                        </th>
                        <th
                          scope="col"
                          className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                        >
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {currentLeads.map((lead: Lead) => {
                        const needsFollowUp = leadNeedsFollowUp(lead);
                        const isSelected = selectedLeads.has(lead.id);
                        return (
                        <tr
                          key={lead.id}
                          className={[
                            isSelected ? "bg-blue-50 dark:bg-blue-900/20" : "",
                            needsFollowUp && !isSelected
                              ? "bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500"
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <td className="whitespace-nowrap py-4 pl-0 text-sm font-medium text-gray-900 dark:text-white">
                            <div className="flex items-center gap-3">
                              {isSelectMode && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleLeadSelection(lead.id)}
                                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                              )}
                              <UserIcon className="h-4 w-4 text-gray-400" />
                              <span>{DOMPurify.sanitize(lead.name, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}</span>
                              {needsFollowUp && (
                                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                  New
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                              <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                              <span>{lead.email ? DOMPurify.sanitize(lead.email, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }) : ''}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-400">
                              <PhoneIcon className="h-4 w-4" />
                              <span>{formatPhoneNumber(lead.phone)}</span>
                              {lead.sms_consent && (
                                <span
                                  className="text-green-600 dark:text-green-400 ml-1"
                                  title="SMS consent provided"
                                >
                                  📱
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center">
                              <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 mr-2">
                                <div
                                  className={`h-2.5 rounded-full ${
                                    (lead.ai_conversion_probability ?? 0) < 0.3
                                      ? "bg-red-500"
                                      : (lead.ai_conversion_probability ?? 0) <
                                        0.7
                                      ? "bg-yellow-500"
                                      : "bg-green-500"
                                  }`}
                                  style={{
                                    width: `${
                                      (lead.ai_conversion_probability ?? 0) *
                                      100
                                    }%`,
                                  }}
                                />
                              </div>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {(
                                  (lead.ai_conversion_probability ?? 0) * 100
                                ).toFixed(0)}
                                %
                              </span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center space-x-2">
                              <CalendarIcon className="h-4 w-4 text-gray-400" />
                              <span>{formatDate(lead.follow_up_date)}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {lastCheckedInMap[lead.id]
                              ? new Date(
                                  lastCheckedInMap[lead.id]
                                ).toLocaleDateString()
                              : "Never"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center space-x-2">
                              <ClockIcon className="h-4 w-4 text-gray-400" />
                              <span>{formatDate(lead.date_added)}</span>
                            </div>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <button
                              type="button"
                              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2 mr-4"
                              onClick={() => handleEditClick(lead)}
                            >
                              <PencilIcon className="h-5 w-5 pointer-events-none" />
                            </button>
                            <button
                              type="button"
                              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2 mr-4"
                              onClick={() => handleConvertClick(lead)}
                            >
                              <CheckIcon className="h-5 w-5 pointer-events-none" />
                            </button>
                            <button
                              type="button"
                              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2"
                              onClick={() => handleDeleteLead(lead)}
                            >
                              <TrashIcon className="h-5 w-5 pointer-events-none" />
                            </button>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center mt-6 space-x-2">
                <button
                  className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <span className="pointer-events-none">Previous</span>
                </button>
                <div className="space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={`px-3 py-1 rounded font-medium border transition-colors duration-150 ${
                        currentPage === i + 1
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                      }`}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      <span className="pointer-events-none">{i + 1}</span>
                    </button>
                  ))}
                </div>
                <button
                  className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  <span className="pointer-events-none">Next</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {leadToDelete && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={cancelDelete}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Delete Lead
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete {leadToDelete.name}?
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={confirmDelete}
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">Delete</span>
                  </button>
                  <button
                    type="button"
                    onClick={cancelDelete}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Convert to Student Modal */}
        {leadToConvert && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleConvertCancel}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Convert Lead to Student
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to convert {leadToConvert.name} to
                      a student? This will:
                    </p>
                    <ul className="mt-2 list-disc list-inside text-sm text-gray-500 dark:text-gray-400 space-y-1">
                      <li>Create a new student record</li>
                      <li>Transfer all lead information</li>
                      <li>Archive this lead</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleConvertConfirm}
                    className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">Convert to Student</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleConvertCancel}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Modal */}
        {showBulkDeleteModal && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => setShowBulkDeleteModal(false)}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Delete Selected Leads
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete {selectedLeads.size} selected lead{selectedLeads.size !== 1 ? 's' : ''} from the current page? This action cannot be undone.
                    </p>
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Leads to be deleted:</p>
                      <ul className="mt-2 text-sm text-gray-500 dark:text-gray-400 space-y-1">
                        {leads
                          .filter(lead => selectedLeads.has(lead.id))
                          .slice(0, 10)
                          .map(lead => (
                            <li key={lead.id} className="flex items-center gap-2">
                              <span>• {lead.name}</span>
                              {lead.email && <span className="text-gray-400">({lead.email})</span>}
                            </li>
                          ))}
                        {selectedLeads.size > 10 && (
                          <li className="text-gray-500">... and {selectedLeads.size - 10} more</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={confirmBulkDelete}
                    disabled={isBulkDeleting}
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">{isBulkDeleting ? "Deleting..." : "Delete Selected"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowBulkDeleteModal(false)}
                    disabled={isBulkDeleting}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
